import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  tenantApi,
  type Group,
  type Role,
  type ScimProvisioningConfig,
  type TenantSettings,
} from '../../services/tenantApi';
import { authService } from '../../services/authService';
import { ExchangeEmailSendersManagement } from './ExchangeEmailSendersManagement';

const SMS_GATEWAY_ALERT_MODE_OPTIONS = [
  { value: 'Disabled', label: 'Disabled' },
  { value: 'TenantAdminDashboard', label: 'Tenant admin dashboard only' },
  { value: 'TenantAndSuperAdminDashboard', label: 'Tenant admin and super admin dashboards' },
];
const SCIM_ADMIN_CONSENT_MARKER_KEY = 'zurvit_scim_admin_consent';
const SCIM_ADMIN_CONSENT_EVENT = 'zurvit-scim-admin-consent';

const TENANT_RESTRICTED_ROLES = new Set(['PlatformAdmin', 'PortalAdmin']);
type TenantAuthMode = 'MagicLinkOnly' | 'Dual' | 'SSOPreferred' | 'SSORequired';

function normalizeTenantAuthMode(raw: string | undefined | null): TenantAuthMode {
  if (!raw) return 'Dual';
  const value = raw.trim().toLowerCase();
  if (value === 'magiclinkonly') return 'MagicLinkOnly';
  if (value === 'ssorequired') return 'SSORequired';
  if (value === 'ssopreferred') return 'SSOPreferred';
  return 'Dual';
}

type Props = {
  onOpenSubscriptions?: () => void;
  onOpenUsersGroups?: () => void;
};

type ScimChecklistStatus = 'done' | 'action' | 'next' | 'in-progress';

export const TenantSettingsManagement: React.FC<Props> = ({ onOpenSubscriptions, onOpenUsersGroups }) => {
  const [settingsTab, setSettingsTab] = useState<'general' | 'email-senders'>('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectingEntra, setConnectingEntra] = useState(false);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [plan, setPlan] = useState('');
  const [authType, setAuthType] = useState<TenantAuthMode>('Dual');
  const [entraTenantId, setEntraTenantId] = useState('');
  const [ssoAutoProvisionUsers, setSsoAutoProvisionUsers] = useState(true);
  const [defaultNewUserRole, setDefaultNewUserRole] = useState('Viewer');
  const [defaultNewUserGroupId, setDefaultNewUserGroupId] = useState('');
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [retentionDays, setRetentionDays] = useState('30');
  const [smsGatewayAlertMode, setSmsGatewayAlertMode] = useState('TenantAndSuperAdminDashboard');
  const [disconnectingEntra, setDisconnectingEntra] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectConfirmation, setDisconnectConfirmation] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scimConfig, setScimConfig] = useState<ScimProvisioningConfig | null>(null);
  const [scimGroupCount, setScimGroupCount] = useState<number>(0);
  const [scimSaving, setScimSaving] = useState(false);
  const [scimPreflighting, setScimPreflighting] = useState(false);
  const [, setScimPreflightSummary] = useState<string>('');
  const [scimApprovalUrl, setScimApprovalUrl] = useState<string>('');
  const [scimEnabled, setScimEnabled] = useState(false);
  const [scimProvisioningMode, setScimProvisioningMode] = useState<'Automatic' | 'Manual'>('Automatic');
  const [scimAuthorityTenantId, setScimAuthorityTenantId] = useState('');
  const [scimBaseUrl, setScimBaseUrl] = useState('');
  const [scimClientId, setScimClientId] = useState('');
  const [scimClientSecretRef, setScimClientSecretRef] = useState('');
  const [scimTestMode, setScimTestMode] = useState(true);
  const [showScimGuideModal, setShowScimGuideModal] = useState(false);
  const [scimPreflightChecked, setScimPreflightChecked] = useState(false);
  const [scimPreflightApprovalRequired, setScimPreflightApprovalRequired] = useState(false);
  const [scimProvisionSucceeded, setScimProvisionSucceeded] = useState(false);
  const [showScimOnboardingPanel, setShowScimOnboardingPanel] = useState(true);
  const [showScimAdvancedPanel, setShowScimAdvancedPanel] = useState(false);
  const [allowScimIdentityEdit, setAllowScimIdentityEdit] = useState(false);

  const applyAdminConsentMarker = useCallback((raw: string | null) => {
    if (!raw) {
      return;
    }

    try {
      const payload = JSON.parse(raw) as {
        consented?: boolean;
        tenant?: string;
      };

      if (!payload?.consented) {
        return;
      }

      try {
        const token = localStorage.getItem('authToken') || '';
        const refreshToken = localStorage.getItem('refreshToken') || '';
        if (token) {
          authService.adoptSessionTokens(token, refreshToken);
          const authStateRaw = sessionStorage.getItem('auth_state');
          const authState = authStateRaw ? JSON.parse(authStateRaw) : {};
          authState.token = token;
          authState.refreshToken = refreshToken;
          sessionStorage.setItem('auth_state', JSON.stringify(authState));
        }
      } catch {
        // Best-effort session token sync after consent popup flow.
      }

      const tenantSuffix = payload.tenant ? ` for tenant ${payload.tenant}` : '';
      setMessage(`Admin consent completed${tenantSuffix}. Next: click Check SCIM Readiness once to continue provisioning.`);
      setError(null);
      setScimPreflightSummary('Admin consent completed. Click Check SCIM Readiness to continue.');
    } catch {
      // Ignore malformed callback markers.
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [result, roles, groups] = await Promise.all([
          tenantApi.getTenantSettings(),
          tenantApi.getRoles(),
          tenantApi.getGroups(),
        ]);

        setAvailableRoles((roles || []).filter((r) => !TENANT_RESTRICTED_ROLES.has(r.name)));
        setAvailableGroups(groups || []);
        setSettings(result);
        setName(result.name || '');
        setRegion(result.region || '');
        setPlan(result.plan || '');
        setAuthType(normalizeTenantAuthMode(result.authType));
        setEntraTenantId(result.entraTenantId || '');
        setSsoAutoProvisionUsers(result.ssoAutoProvisionUsers ?? true);
        setDefaultNewUserRole(result.defaultNewUserRole || 'Viewer');
        setDefaultNewUserGroupId(result.defaultNewUserGroupId || '');
        setRetentionDays(String(result.retentionDays || 30));
        setSmsGatewayAlertMode(result.smsGatewayAlertMode || 'TenantAndSuperAdminDashboard');

        try {
          const [scim, scimGroups] = await Promise.all([
            tenantApi.getTenantScimConfig(),
            tenantApi.getTenantScimGroupIdentities(100),
          ]);
          setScimConfig(scim);
          setScimGroupCount(scimGroups?.count || 0);
          setScimEnabled(!!scim?.isEnabled);
          setScimProvisioningMode((scim?.provisioningMode || 'Automatic') as 'Automatic' | 'Manual');
          setScimAuthorityTenantId(scim?.authorityTenantId || '');
          setScimBaseUrl(scim?.scimBaseUrl || '');
          setScimClientId(scim?.clientId || '');
          setScimClientSecretRef(scim?.clientSecretRef || '');
          setScimTestMode(scim?.isTestMode ?? true);
          setScimProvisionSucceeded(!!(scim?.clientId || '').trim());
        } catch {
          setScimConfig(null);
          setScimGroupCount(0);
          setScimProvisionSucceeded(false);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load tenant settings');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  useEffect(() => {
    const consumeLocalMarker = (raw: string | null) => {
      applyAdminConsentMarker(raw);
      if (raw) {
        localStorage.removeItem(SCIM_ADMIN_CONSENT_MARKER_KEY);
      }
    };

    consumeLocalMarker(localStorage.getItem(SCIM_ADMIN_CONSENT_MARKER_KEY));

    const onStorage = (event: StorageEvent) => {
      if (event.key === SCIM_ADMIN_CONSENT_MARKER_KEY && event.newValue) {
        consumeLocalMarker(event.newValue);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data as {
        type?: string;
        payload?: unknown;
      } | null;

      if (!data || data.type !== SCIM_ADMIN_CONSENT_EVENT || !data.payload) {
        return;
      }

      consumeLocalMarker(JSON.stringify(data.payload));
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('message', onMessage);
    };
  }, [applyAdminConsentMarker]);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return (
      (name || '').trim() !== (settings.name || '')
      || Number(retentionDays || 0) !== Number(settings.retentionDays || 0)
      || (ssoAutoProvisionUsers !== (settings.ssoAutoProvisionUsers ?? true))
      || (defaultNewUserRole || 'Viewer') !== (settings.defaultNewUserRole || 'Viewer')
      || (defaultNewUserGroupId || '') !== (settings.defaultNewUserGroupId || '')
      || (smsGatewayAlertMode || '') !== (settings.smsGatewayAlertMode || 'TenantAndSuperAdminDashboard')
    );
  }, [settings, name, retentionDays, ssoAutoProvisionUsers, defaultNewUserRole, defaultNewUserGroupId, smsGatewayAlertMode]);

  const onSave = async () => {
    setMessage(null);
    setError(null);

    const retention = Number(retentionDays);
    if (!Number.isFinite(retention) || retention < 1 || retention > 3650) {
      setError('Retention days must be between 1 and 3650.');
      return;
    }

    setSaving(true);
    try {
      const updated = await tenantApi.updateTenantSettings({
        name: name.trim(),
        ssoAutoProvisionUsers,
        defaultNewUserRole: (defaultNewUserRole || '').trim() || 'Viewer',
        defaultNewUserGroupId: defaultNewUserGroupId || '',
        retentionDays: retention,
        smsGatewayAlertMode,
      });
      setSettings(updated);
      setName(updated.name || '');
      setRegion(updated.region || '');
      setPlan(updated.plan || '');
      setSsoAutoProvisionUsers(updated.ssoAutoProvisionUsers ?? true);
      setDefaultNewUserRole(updated.defaultNewUserRole || 'Viewer');
      setDefaultNewUserGroupId(updated.defaultNewUserGroupId || '');
      setRetentionDays(String(updated.retentionDays || retention));
      setSmsGatewayAlertMode(updated.smsGatewayAlertMode || 'TenantAndSuperAdminDashboard');
      setMessage('Tenant settings saved.');
      window.dispatchEvent(new CustomEvent('nog:tenant-settings:changed', { detail: updated }));
    } catch (err: any) {
      setError(err?.message || 'Failed to save tenant settings');
    } finally {
      setSaving(false);
    }
  };

  // Connected state must come from persisted tenant settings, not draft textbox input.
  // Otherwise typing/pasting a tenant ID incorrectly flips the form to read-only.
  const isEntraConnected = ((settings?.entraTenantId) || '').trim().length > 0;
  const isSsoEnabled = authType !== 'MagicLinkOnly';
  const isScimSsoReady = isEntraConnected && isSsoEnabled;
  const recommendedScimSecretRef = useMemo(() => {
    const slug = (settings?.slug || '').trim().toLowerCase();
    return slug ? `${slug}-scim-client-secret` : 'tenant-scim-client-secret';
  }, [settings?.slug]);

  const effectiveScimSecretRef = useMemo(() => {
    const explicit = scimClientSecretRef.trim();
    return explicit || recommendedScimSecretRef;
  }, [scimClientSecretRef, recommendedScimSecretRef]);

  useEffect(() => {
    // Default to a tenant-scoped secret ref so admins are not blocked on manual entry.
    if (!scimClientSecretRef.trim() && recommendedScimSecretRef) {
      setScimClientSecretRef(recommendedScimSecretRef);
    }
  }, [recommendedScimSecretRef, scimClientSecretRef]);

  const scimSetupChecklist = useMemo(() => ({
    ssoReady: isScimSsoReady,
    authorityReady: !!(scimAuthorityTenantId.trim() || (isEntraConnected && entraTenantId.trim())),
    clientReady: !!scimClientId.trim(),
    secretReady: !!effectiveScimSecretRef.trim(),
  }), [isScimSsoReady, scimAuthorityTenantId, isEntraConnected, entraTenantId, scimClientId, effectiveScimSecretRef]);

  const scimVisualChecklist = useMemo(() => {
    const authorityReady = scimSetupChecklist.authorityReady;
    const consentDone = scimPreflightChecked && !scimPreflightApprovalRequired;
    const mappingReady = scimGroupCount > 0;

    const steps: Array<{ key: string; title: string; status: ScimChecklistStatus; detail: string }> = [
      {
        key: 'sso',
        title: 'Connect Entra and enable SSO mode',
        status: isScimSsoReady ? 'done' : 'action',
        detail: isScimSsoReady
          ? 'Entra connection and SSO mode are configured.'
          : 'Connect Entra and set SSO mode to Dual, SSO Preferred, or SSO Required.',
      },
      {
        key: 'authority',
        title: 'Confirm authority tenant and secret reference',
        status: authorityReady && !!effectiveScimSecretRef.trim() ? 'done' : (isScimSsoReady ? 'action' : 'next'),
        detail: authorityReady
          ? 'Authority Tenant ID auto-resolves from Entra binding. Secret reference defaults to a tenant-scoped value in the shared platform Key Vault.'
          : 'Set Authority Tenant ID (or rely on Entra binding default) before provisioning.',
      },
      {
        key: 'consent',
        title: 'Run readiness and complete admin consent if requested',
        status: consentDone
          ? 'done'
          : (scimPreflighting ? 'in-progress' : (scimPreflightApprovalRequired ? 'action' : 'next')),
        detail: consentDone
          ? 'Readiness passed and consent gate is satisfied.'
          : (scimPreflightApprovalRequired
            ? 'Authorize SCIM setup in the new tab. Admin consent appears only when the required permissions were not granted during SSO onboarding.'
            : 'Click Check SCIM Readiness to validate permissions and continue.'),
      },
      {
        key: 'provision',
        title: 'Provision tenant SCIM app registration',
        status: (scimProvisionSucceeded && consentDone)
          ? 'done'
          : (scimPreflighting ? 'in-progress' : (consentDone ? 'action' : 'next')),
        detail: (scimProvisionSucceeded && consentDone)
          ? 'SCIM app registration is provisioned and client ID is available.'
          : (scimProvisionSucceeded
            ? 'Existing SCIM client ID detected. Complete readiness first, then re-run provisioning to validate end-to-end.'
            : 'This runs automatically after readiness passes. If it fails, review the top error and retry once host is updated.'),
      },
      {
        key: 'mapping',
        title: 'Map source groups in Users & Groups',
        status: mappingReady ? 'done' : (scimProvisionSucceeded ? 'action' : 'next'),
        detail: mappingReady
          ? `Configured source groups: ${scimGroupCount}.`
          : 'Open Users & Groups Mapping to complete group-level SCIM onboarding.',
      },
    ];

    return steps;
  }, [
    isScimSsoReady,
    scimSetupChecklist.authorityReady,
    effectiveScimSecretRef,
    scimPreflightChecked,
    scimPreflightApprovalRequired,
    scimPreflighting,
    scimProvisionSucceeded,
    scimGroupCount,
  ]);

  const scimIdentityLocked = !!scimClientId.trim() && !allowScimIdentityEdit;

  const onCheckScimReadiness = async () => {
    setError(null);
    setMessage(null);
    setScimPreflightSummary('');
    setScimApprovalUrl('');
    setScimPreflightChecked(false);
    setScimPreflightApprovalRequired(false);
    setScimPreflighting(true);

    if (!isScimSsoReady) {
      setScimPreflighting(false);
      setError('Connect Entra and enable SSO mode before checking SCIM readiness.');
      return;
    }

    const resolvedAuthorityTenantId = scimAuthorityTenantId.trim() || (isEntraConnected ? entraTenantId.trim() : '');
    const resolvedSecretRef = scimClientSecretRef.trim() || recommendedScimSecretRef;
    setScimAuthorityTenantId(resolvedAuthorityTenantId);
    setScimClientSecretRef(resolvedSecretRef);

    try {
      const result = await tenantApi.preflightTenantScimProvisioning();
      const missingRoles = Array.isArray(result.missingRoles) ? result.missingRoles : [];
      const missingSegment = missingRoles.length > 0 ? ` Missing roles: ${missingRoles.join(', ')}.` : '';
      const reachabilitySegment = result.graphReachable ? ' Graph reachable.' : (result.approvalRequired ? '' : ' Graph not reachable.');
      const statusSegment = result.canProvision ? 'Readiness passed.' : (result.approvalRequired ? 'Approval required.' : 'Readiness failed.');
      const approvalSegment = result.approvalRequired ? ' Tenant admin consent is required before provisioning can continue.' : '';
      const summary = `${statusSegment}${reachabilitySegment}${missingSegment}${approvalSegment} ${result.message || ''}`.trim();
      setScimPreflightSummary(summary);
      setScimApprovalUrl(result.approvalUrl || '');
      setScimPreflightChecked(!!result.canProvision);
      setScimPreflightApprovalRequired(!!result.approvalRequired);

      if (!result.canProvision) {
        setError(summary);
        return;
      }

      const response = await tenantApi.provisionTenantScimApp({
        clientSecretRef: resolvedSecretRef,
        enableScimAfterProvisioning: true,
      });

      setScimEnabled(response.isScimEnabled);
      setScimAuthorityTenantId(response.authorityTenantId || resolvedAuthorityTenantId);
      setScimClientId(response.clientId || '');
      setScimClientSecretRef(response.clientSecretRef || resolvedSecretRef);
      setScimProvisionSucceeded(!!(response.clientId || '').trim());

      const refreshed = await tenantApi.getTenantScimConfig();
      setScimConfig(refreshed);
      setScimProvisioningMode((refreshed.provisioningMode || 'Automatic') as 'Automatic' | 'Manual');
      setScimBaseUrl(refreshed.scimBaseUrl || '');
      setScimTestMode(refreshed.isTestMode ?? true);

      const reuseText = response.reusedExistingApp ? ' Existing app was reused.' : '';
      setMessage(`${summary} ${(response.message || 'SCIM app is provisioned and ready.')}${reuseText}`.trim());
    } catch (err: any) {
      if (err?.approvalUrl) {
        setScimApprovalUrl(err.approvalUrl);
      }
      setScimPreflightChecked(false);
      setScimPreflightApprovalRequired(!!err?.approvalUrl || err?.status === 409);
      const msg = err?.message || 'Failed to check SCIM readiness';
      setScimPreflightSummary(msg);
      setScimProvisionSucceeded(false);
      setError(msg);
    } finally {
      setScimPreflighting(false);
    }
  };

  const onOpenAdminConsentRequest = () => {
    if (scimPreflighting) {
      return;
    }

    if (!scimApprovalUrl) {
      setError('Admin consent link is unavailable because OAuth client configuration is incomplete. Ensure EntraClientId is configured (or provide dedicated ScimConsentClientId), then retry Check SCIM Readiness.');
      setMessage(null);
      return;
    }

    window.open(scimApprovalUrl, '_blank', 'noopener,noreferrer');
    setMessage('SCIM authorization opened in a new tab. If consent was already granted during SSO onboarding, no second consent prompt is required. Return here and click Check SCIM Readiness once.');
    setError(null);
  };

  const onApplyRecommendedSecretRef = () => {
    setScimClientSecretRef(recommendedScimSecretRef);
    setMessage(`SCIM secret reference set to ${recommendedScimSecretRef}.`);
    setError(null);
  };

  const normalizedDisconnectConfirmation = disconnectConfirmation.replace(/["“”]/g, '').trim();
  const isDisconnectConfirmed = normalizedDisconnectConfirmation === 'I AGREE';

  const onConnectEntra = async () => {
    setMessage(null);
    setError(null);

    if (isEntraConnected) {
      setError('Entra is already connected. Disconnect first to change the tenant binding.');
      return;
    }

    if (authType === 'MagicLinkOnly') {
      setError('Select an SSO mode (Dual, SSO Preferred, or SSO Required) before connecting Entra.');
      return;
    }

    if (!entraTenantId.trim()) {
      setError('Entra Tenant ID is required.');
      return;
    }

    setConnectingEntra(true);
    try {
      const requestedEntraTenantId = entraTenantId.trim();
      const connection = await tenantApi.connectTenantEntraIdentityProvider({
        entraTenantId: requestedEntraTenantId,
        authType,
      });

      if (!connection.adminConsentUrl) {
        throw new Error('Unable to start Entra admin consent. Verify EntraClientId and redirect URI configuration.');
      }

      window.location.assign(connection.adminConsentUrl);
    } catch (err: any) {
      setError(err?.message || 'Failed to connect Entra identity provider');
    } finally {
      setConnectingEntra(false);
    }
  };

  const onDisconnectEntra = async () => {
    setMessage(null);
    setError(null);

    if (!isEntraConnected) {
      setError('Entra is not currently connected for this tenant.');
      return;
    }

    if (!isDisconnectConfirmed) {
      setError('Type I AGREE exactly to confirm Entra disconnect.');
      return;
    }

    setDisconnectingEntra(true);
    try {
      await tenantApi.disconnectTenantEntraIdentityProvider({ confirmationText: 'I AGREE' });

      const refreshed = await tenantApi.getTenantSettings();
      setSettings(refreshed);
      setName(refreshed.name || '');
      setRegion(refreshed.region || '');
      setPlan(refreshed.plan || '');
      setAuthType(normalizeTenantAuthMode(refreshed.authType));
      setEntraTenantId(refreshed.entraTenantId || '');
      setSsoAutoProvisionUsers(refreshed.ssoAutoProvisionUsers ?? true);
      setDefaultNewUserRole(refreshed.defaultNewUserRole || 'Viewer');
      setDefaultNewUserGroupId(refreshed.defaultNewUserGroupId || '');
      setRetentionDays(String(refreshed.retentionDays || 30));
      setSmsGatewayAlertMode(refreshed.smsGatewayAlertMode || 'TenantAndSuperAdminDashboard');

      setShowDisconnectModal(false);
      setDisconnectConfirmation('');
      setMessage('Entra identity provider disconnected. Authentication mode has reverted to Magic Link only.');
    } catch (err: any) {
      setError(err?.message || 'Failed to disconnect Entra identity provider');
    } finally {
      setDisconnectingEntra(false);
    }
  };

  const onReset = () => {
    if (!settings) return;
    setName(settings.name || '');
    setRegion(settings.region || '');
    setPlan(settings.plan || '');
    setAuthType(normalizeTenantAuthMode(settings.authType));
    setEntraTenantId(settings.entraTenantId || '');
    setSsoAutoProvisionUsers(settings.ssoAutoProvisionUsers ?? true);
    setDefaultNewUserRole(settings.defaultNewUserRole || 'Viewer');
    setDefaultNewUserGroupId(settings.defaultNewUserGroupId || '');
    setRetentionDays(String(settings.retentionDays || 30));
    setSmsGatewayAlertMode(settings.smsGatewayAlertMode || 'TenantAndSuperAdminDashboard');
    setMessage(null);
    setError(null);

    if (scimConfig) {
      setScimEnabled(!!scimConfig.isEnabled);
      setScimProvisioningMode((scimConfig.provisioningMode || 'Automatic') as 'Automatic' | 'Manual');
      setScimAuthorityTenantId(scimConfig.authorityTenantId || '');
      setScimBaseUrl(scimConfig.scimBaseUrl || '');
      setScimClientId(scimConfig.clientId || '');
      setScimClientSecretRef(scimConfig.clientSecretRef || '');
      setScimTestMode(scimConfig.isTestMode ?? true);
    }
  };

  const onSaveScim = async () => {
    setMessage(null);
    setError(null);

    const effectiveAuthorityTenantId = scimAuthorityTenantId.trim() || (isEntraConnected ? entraTenantId.trim() : '');

    if (scimEnabled) {
      if (!effectiveAuthorityTenantId) {
        setError('SCIM Authority Tenant ID is required when SCIM is enabled.');
        return;
      }
      if (!scimClientId.trim()) {
        setError('SCIM Client ID is required when SCIM is enabled.');
        return;
      }
      if (!scimClientSecretRef.trim()) {
        setError('SCIM Client Secret Ref is required when SCIM is enabled.');
        return;
      }
    }

    setScimSaving(true);
    try {
      const updated = await tenantApi.updateTenantScimConfig({
        isEnabled: scimEnabled,
        provisioningMode: scimProvisioningMode,
        authorityTenantId: effectiveAuthorityTenantId || undefined,
        scimBaseUrl: scimBaseUrl.trim() || undefined,
        clientId: scimClientId.trim() || undefined,
        clientSecretRef: scimClientSecretRef.trim() || undefined,
        isTestMode: scimTestMode,
      });

      const scimGroups = await tenantApi.getTenantScimGroupIdentities(100).catch(() => ({ count: scimGroupCount } as any));

      setScimConfig(updated);
      setScimGroupCount(scimGroups?.count || 0);
      setScimEnabled(!!updated.isEnabled);
      setScimProvisioningMode((updated.provisioningMode || 'Automatic') as 'Automatic' | 'Manual');
      setScimAuthorityTenantId(updated.authorityTenantId || effectiveAuthorityTenantId || '');
      setScimBaseUrl(updated.scimBaseUrl || '');
      setScimClientId(updated.clientId || '');
      setScimClientSecretRef(updated.clientSecretRef || '');
      setScimTestMode(updated.isTestMode ?? true);
      setMessage('SCIM configuration saved.');
    } catch (err: any) {
      setError(err?.message || 'Failed to save SCIM configuration');
    } finally {
      setScimSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading tenant settings...</div>;
  }

  if (!settings) {
    return (
      <div className="management-section tenant-settings-view">
        <div className="error-message">{error || 'Unable to load tenant settings.'}</div>
      </div>
    );
  }

  if (settingsTab === 'email-senders') {
    return (
      <div className="management-section tenant-settings-view">
        <div className="tenant-settings-tabs" role="tablist" aria-label="Tenant settings sections">
          <button type="button" role="tab" aria-selected="false" onClick={() => setSettingsTab('general')}>General</button>
          <button type="button" role="tab" aria-selected="true">Email Senders</button>
        </div>
        <ExchangeEmailSendersManagement />
      </div>
    );
  }

  return (
    <div className="management-section tenant-settings-view">
      <div className="tenant-settings-tabs" role="tablist" aria-label="Tenant settings sections">
        <button type="button" role="tab" aria-selected="true">General</button>
        <button type="button" role="tab" aria-selected="false" onClick={() => setSettingsTab('email-senders')}>Email Senders</button>
      </div>
      <div className="tenant-settings-sticky-heading">
        {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div>}
        {message && <div className="success-message" style={{ marginBottom: '0.75rem' }}>{message}</div>}
        <div className="form-actions" style={{ marginTop: 0, paddingTop: 0, borderTop: 0, marginBottom: 0 }}>
          <button className="btn-secondary" onClick={onReset} disabled={!dirty || saving}>Reset</button>
          <button className="btn-primary" onClick={onSave} disabled={!dirty || saving}>
            {saving ? 'Saving...' : 'Save Tenant Settings'}
          </button>
        </div>
      </div>

      <div className="data-table tenant-settings-subcard" style={{ padding: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>General</h3>
        <div className="help-text" style={{ marginBottom: '0.75rem' }}>
          Retention controls automated alert/event lifecycle cleanup windows.
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Tenant Name</label>
            <input value={name} readOnly />
          </div>
          <div className="form-group">
            <label>Slug</label>
            <input value={settings?.slug || ''} readOnly />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Current Region</label>
            <input value={region || 'Unknown'} readOnly />
          </div>
          <div className="form-group">
            <label>Current Plan</label>
            <div className="tenant-plan-textbox-row">
              <input value={plan || 'Unknown'} readOnly />
              <button type="button" className="btn-secondary" onClick={onOpenSubscriptions} disabled={!onOpenSubscriptions}>
                Change Plan
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Retention (days)</label>
            <input
              type="number"
              min={1}
              max={3650}
              value={retentionDays}
              onChange={(e) => setRetentionDays(e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Auth Type</label>
            <input value={settings?.authType || ''} readOnly />
          </div>
          <div className="form-group">
            <label>Status</label>
            <input value={settings?.status || ''} readOnly />
          </div>
        </div>
      </div>

      <div className="data-table tenant-settings-subcard" style={{ padding: '1rem', marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Identity Providers</h3>
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Microsoft Entra Tenant ID</label>
            <input
              value={entraTenantId}
              onChange={(e) => setEntraTenantId(e.target.value)}
              placeholder="tenant-id.onmicrosoft.com or GUID"
              readOnly={isEntraConnected}
              disabled={isEntraConnected}
            />
            <div className="help-text">Used to validate and bind this tenant to the correct Entra directory.</div>
          </div>
          <div className="form-group">
            <label>SSO Mode</label>
            <select value={authType} onChange={(e) => setAuthType(e.target.value as TenantAuthMode)}>
              <option value="MagicLinkOnly">Magic Link only</option>
              <option value="Dual">Dual (Magic Link + SSO)</option>
              <option value="SSOPreferred">SSO Preferred</option>
              <option value="SSORequired">SSO Required</option>
            </select>
          </div>
        </div>
        <div className="form-actions" style={{ marginTop: '0.25rem' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={isEntraConnected ? () => setShowDisconnectModal(true) : onConnectEntra}
            disabled={connectingEntra || disconnectingEntra}
          >
            {isEntraConnected
              ? (disconnectingEntra ? 'Disconnecting...' : 'Disconnect Entra')
              : (connectingEntra ? 'Connecting...' : 'Connect Entra')}
          </button>
        </div>

        <div className="form-row" style={{ marginTop: '0.5rem' }}>
          <div className="form-group" style={{ flex: 2 }}>
            <label>SSO Access Provisioning</label>
            <select
              value={ssoAutoProvisionUsers ? 'AllowAll' : 'InvitationOnly'}
              onChange={(e) => setSsoAutoProvisionUsers(e.target.value === 'AllowAll')}
            >
              <option value="AllowAll">Allow all directory users (auto-provision)</option>
              <option value="InvitationOnly">Invitation only (must already be a tenant user)</option>
            </select>
            <div className="help-text" style={{ marginTop: '0.5rem' }}>
              Controls whether any Entra user from the bound directory can sign in and be created automatically, or only users already invited/provisioned in this tenant can sign in.
            </div>
          </div>
        </div>

        <div className="form-row" style={{ marginTop: '0.5rem' }}>
          <div className="form-group">
            <label>Default New-User Role</label>
            <select
              value={defaultNewUserRole || 'Viewer'}
              onChange={(e) => setDefaultNewUserRole(e.target.value)}
            >
              <option value="Viewer">Viewer</option>
              {availableRoles
                .filter((r) => r.name !== 'Viewer')
                .map((role) => <option key={role.roleId} value={role.name}>{role.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Default New-User Group (optional)</label>
            <select
              value={defaultNewUserGroupId}
              onChange={(e) => setDefaultNewUserGroupId(e.target.value)}
            >
              <option value="">No default group</option>
              {availableGroups.map((group) => <option key={group.groupId} value={group.groupId}>{group.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="data-table tenant-settings-subcard" style={{ padding: '1rem', marginTop: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>SCIM Provisioning (P0 Baseline)</h3>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowScimGuideModal(true)}
          >
            How to Use SCIM
          </button>
        </div>
        <div className="help-text" style={{ marginBottom: '0.75rem' }}>
          Configure tenant SCIM baseline provisioning and source-group diagnostics.
        </div>

        <div className="form-actions" style={{ marginTop: 0, marginBottom: '0.8rem', paddingTop: 0, borderTop: 0 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowScimOnboardingPanel((prev) => !prev)}
          >
            {showScimOnboardingPanel ? 'Hide Onboarding Checklist' : 'Show Onboarding Checklist'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowScimAdvancedPanel((prev) => !prev)}
          >
            {showScimAdvancedPanel ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
          </button>
        </div>

        {showScimOnboardingPanel && (
          <div
            style={{
              border: '1px solid var(--border-color, #d9e1ec)',
              borderRadius: 8,
              padding: '0.75rem',
              marginBottom: '0.9rem',
              background: 'var(--card-bg)'
            }}
          >
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>SCIM Onboarding Checklist</div>
          <div className="help-text" style={{ marginBottom: '0.65rem' }}>
            Follow these steps in order. Status updates automatically as checks run.
          </div>
          <div style={{ display: 'grid', gap: '0.55rem' }}>
            {scimVisualChecklist.map((step, index) => {
              const badgeLabel = step.status === 'done'
                ? 'Done'
                : step.status === 'action'
                  ? 'Action Required'
                  : step.status === 'in-progress'
                    ? 'In Progress'
                    : 'Next';

              const badgeStyles = step.status === 'done'
                ? { background: 'rgba(23, 163, 74, 0.14)', color: 'var(--color-success, #14833b)', border: '1px solid rgba(23, 163, 74, 0.3)' }
                : step.status === 'action'
                  ? { background: 'rgba(255, 149, 0, 0.14)', color: '#9a5b00', border: '1px solid rgba(255, 149, 0, 0.34)' }
                  : step.status === 'in-progress'
                    ? { background: 'rgba(0, 122, 204, 0.14)', color: '#005a96', border: '1px solid rgba(0, 122, 204, 0.32)' }
                    : { background: 'rgba(128, 128, 128, 0.12)', color: 'var(--color-text-secondary)', border: '1px solid var(--border-color, #d9e1ec)' };

              return (
                <div key={step.key} style={{ border: '1px solid var(--border-color, #d9e1ec)', borderRadius: 8, padding: '0.6rem 0.7rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 600 }}>{index + 1}. {step.title}</div>
                    <span style={{ borderRadius: 999, padding: '0.16rem 0.56rem', fontSize: '0.78rem', fontWeight: 600, ...badgeStyles }}>
                      {badgeLabel}
                    </span>
                  </div>
                  <div className="help-text" style={{ marginTop: '0.32rem' }}>{step.detail}</div>

                  {step.status !== 'done' && (
                    <div className="form-actions" style={{ marginTop: '0.45rem', paddingTop: 0, borderTop: 0, marginBottom: 0 }}>
                      {step.key === 'sso' && (
                        <>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => {
                              setMessage('Go to Identity Providers above this section, then connect Entra and set SSO mode to Dual/Preferred/Required.');
                              setError(null);
                            }}
                          >
                            Guide Me To Identity Setup
                          </button>
                          {!isEntraConnected && (
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={onConnectEntra}
                              disabled={connectingEntra || authType === 'MagicLinkOnly' || !entraTenantId.trim()}
                            >
                              {connectingEntra ? 'Connecting...' : 'Connect Entra Now'}
                            </button>
                          )}
                        </>
                      )}

                      {step.key === 'authority' && (
                        <>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={onApplyRecommendedSecretRef}
                            disabled={effectiveScimSecretRef === recommendedScimSecretRef}
                          >
                            Use Tenant Default Secret Ref
                          </button>
                        </>
                      )}

                      {step.key === 'consent' && (
                        <>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={onCheckScimReadiness}
                            disabled={scimPreflighting}
                          >
                            {scimPreflighting ? 'Checking Readiness...' : 'Run Check SCIM Readiness'}
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={onOpenAdminConsentRequest}
                            disabled={scimPreflighting}
                          >
                            {scimApprovalUrl ? 'Authorize SCIM Setup' : 'Fix SCIM Authorization Setup'}
                          </button>
                        </>
                      )}

                      {step.key === 'provision' && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={onCheckScimReadiness}
                          disabled={scimPreflighting || !scimPreflightChecked}
                        >
                          {scimPreflighting ? 'Provisioning...' : 'Retry Provisioning'}
                        </button>
                      )}

                      {step.key === 'mapping' && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => onOpenUsersGroups?.()}
                          disabled={!onOpenUsersGroups}
                        >
                          Open Users &amp; Groups Mapping
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        )}

        <div className="form-actions" style={{ marginTop: 0, marginBottom: '0.75rem', paddingTop: 0, borderTop: 0 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => onOpenUsersGroups?.()}
            disabled={!onOpenUsersGroups}
          >
            Open Users &amp; Groups Mapping
          </button>
        </div>

        <div
          style={{
            border: `1px solid ${isScimSsoReady ? 'var(--color-success, #17a34a)' : 'var(--border-color, #d9e1ec)'}`,
            borderRadius: 8,
            padding: '0.75rem',
            marginBottom: '0.9rem',
            background: isScimSsoReady ? 'rgba(23, 163, 74, 0.06)' : 'var(--card-bg)'
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
            SSO Readiness: {isScimSsoReady ? 'Ready for SCIM enablement' : 'Action required before SCIM enablement'}
          </div>
          {isScimSsoReady ? (
            <div className="help-text">
              Entra is already connected and SSO mode is enabled. Next: save SCIM settings, then configure SCIM-to-local group mappings in Users &amp; Groups.
            </div>
          ) : (
            <div className="help-text">
              {isEntraConnected
                ? 'Entra is connected, but SSO mode is Magic Link only. Switch SSO Mode to Dual, SSO Preferred, or SSO Required.'
                : 'Connect Entra in Identity Providers and choose an SSO mode (Dual, SSO Preferred, or SSO Required).'}
            </div>
          )}
        </div>

        {showScimAdvancedPanel && (
          <>
            <div className="form-actions" style={{ marginTop: 0, marginBottom: '0.75rem', paddingTop: 0, borderTop: 0 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setAllowScimIdentityEdit((prev) => !prev)}
                disabled={!scimClientId.trim()}
              >
                {scimIdentityLocked ? 'Unlock Client Fields For Edit' : 'Lock Client Fields'}
              </button>
              {scimClientId.trim() && (
                <div className="help-text" style={{ alignSelf: 'center' }}>
                  {scimIdentityLocked
                    ? 'Client ID and secret reference are locked to prevent accidental edits after provisioning.'
                    : 'Client fields are unlocked. Re-lock after intentional updates.'}
                </div>
              )}
            </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>SCIM Status</label>
            <input value={scimEnabled ? 'Enabled' : 'Disabled'} readOnly />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label>Last Sync Status</label>
            <input value={scimConfig?.lastSyncStatus || 'NotConfigured'} readOnly />
          </div>
          <div className="form-group">
            <label>Known Source Groups</label>
            <input value={String(scimGroupCount)} readOnly />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Provisioning</label>
            <select value={scimEnabled ? 'Enabled' : 'Disabled'} onChange={(e) => setScimEnabled(e.target.value === 'Enabled')}>
              <option value="Disabled">Disabled</option>
              <option value="Enabled">Enabled</option>
            </select>
          </div>
          <div className="form-group">
            <label>Provisioning Mode</label>
            <select value={scimProvisioningMode} onChange={(e) => setScimProvisioningMode(e.target.value as 'Automatic' | 'Manual')}>
              <option value="Automatic">Automatic</option>
              <option value="Manual">Manual</option>
            </select>
          </div>
          <div className="form-group">
            <label>Execution Mode</label>
            <select value={scimTestMode ? 'Test' : 'Live'} onChange={(e) => setScimTestMode(e.target.value === 'Test')}>
              <option value="Test">Test</option>
              <option value="Live">Live</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Authority Tenant ID</label>
            <input
              value={scimAuthorityTenantId}
              onChange={(e) => setScimAuthorityTenantId(e.target.value)}
              placeholder="Entra tenant GUID/domain"
              readOnly={isEntraConnected && !!entraTenantId.trim()}
            />
            {isEntraConnected && !scimAuthorityTenantId.trim() && (
              <div className="help-text" style={{ marginTop: '0.35rem' }}>
                Detected Entra binding for this tenant. Authority Tenant ID will default to {entraTenantId} when saved.
              </div>
            )}
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label>SCIM Base URL (optional)</label>
            <input value={scimBaseUrl} onChange={(e) => setScimBaseUrl(e.target.value)} placeholder="https://..." />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Client ID</label>
            <input
              value={scimClientId}
              onChange={(e) => setScimClientId(e.target.value)}
              placeholder="Application (client) ID"
              readOnly={scimIdentityLocked}
            />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label>Client Secret Ref</label>
            <input
              value={scimClientSecretRef}
              onChange={(e) => setScimClientSecretRef(e.target.value)}
              placeholder="Key Vault secret reference name"
              readOnly={scimIdentityLocked}
            />
            <div className="help-text" style={{ marginTop: '0.35rem' }}>
              Defaults to a tenant-scoped value ({recommendedScimSecretRef}). Override only if your tenant uses a custom secret naming convention.
            </div>
          </div>
        </div>

        <div className="form-actions" style={{ marginTop: '0.5rem' }}>
          <button className="btn-secondary" onClick={onSaveScim} disabled={scimSaving}>
            {scimSaving ? 'Saving...' : 'Save SCIM Settings'}
          </button>
        </div>
          </>
        )}
      </div>

      <div className="data-table tenant-settings-subcard" style={{ padding: '1rem', marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>SMS Gateway Alerts</h3>
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Outage Notification Mode</label>
            <select value={smsGatewayAlertMode} onChange={(e) => setSmsGatewayAlertMode(e.target.value)}>
              {SMS_GATEWAY_ALERT_MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <div className="help-text" style={{ marginTop: '0.5rem' }}>
              Controls dashboard notifications when this tenant has no active, connected tenant SMS gateway. This can stay local to tenant admins or also flow up into super admin monitoring.
            </div>
          </div>
        </div>
      </div>

      {showDisconnectModal && (
        <div className="modal-overlay" onClick={() => { if (!disconnectingEntra) setShowDisconnectModal(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Disconnect Entra Identity Provider</h3>
            <div className="warning-message" style={{ marginBottom: '0.8rem' }}>
              Disconnecting Entra will disable tenant SSO for this tenant and revert authentication to Magic Link only.
            </div>
            <div className="help-text" style={{ marginBottom: '0.75rem' }}>
              Implications include:
            </div>
            <ul style={{ marginTop: 0, marginBottom: '0.9rem', paddingLeft: '1.2rem', color: 'var(--color-text-secondary)' }}>
              <li>Users can no longer authenticate via Microsoft SSO until Entra is connected again.</li>
              <li>The Entra app registration and enterprise app are not deleted and may become unmanaged/orphaned from this tenant binding.</li>
              <li>Any SSO onboarding policies for this tenant will stop applying immediately.</li>
            </ul>
            <div className="form-group">
              <label>Type "I AGREE" to confirm (case-sensitive)</label>
              <input
                value={disconnectConfirmation}
                onChange={(e) => setDisconnectConfirmation(e.target.value)}
                placeholder="I AGREE"
                autoFocus
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDisconnectModal(false)}
                disabled={disconnectingEntra}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={onDisconnectEntra}
                disabled={disconnectingEntra || !isDisconnectConfirmed}
              >
                {disconnectingEntra ? 'Disconnecting...' : 'Disconnect Entra'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showScimGuideModal && (
        <div className="modal-overlay" onClick={() => setShowScimGuideModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <h3>SCIM Enablement Guide</h3>
            <div className="help-text" style={{ marginBottom: '0.8rem' }}>
              Follow this sequence to align SCIM onboarding with tenant-admin approval and local group membership mapping.
            </div>

            <ol style={{ marginTop: 0, marginBottom: '0.9rem', paddingLeft: '1.2rem' }}>
              <li>Ensure Identity Providers has Entra connected and SSO Mode is Dual, SSO Preferred, or SSO Required.</li>
              <li>Confirm SSO Access Provisioning is set to Allow all directory users (auto-provision) if you want automatic onboarding.</li>
              <li>Select Check SCIM Readiness. This applies defaults, runs readiness checks, and auto-provisions SCIM app when Graph consent is already in place.</li>
              <li>If readiness reports approval required, use Open Admin Consent Request and have a tenant admin approve, then run Check SCIM Readiness again.</li>
              <li>Save SCIM settings, then go to Users &amp; Groups and ensure local groups exist for each destination membership pattern.</li>
              <li>In each local group, add one or many Entra/SCIM Source Group IDs. Many source groups can map to one local group.</li>
              <li>Test with Execution Mode set to Test, then switch to Live after validation.</li>
            </ol>

            <div className="warning-message" style={{ marginBottom: '0.9rem' }}>
              Open Admin Consent Request is enabled after readiness returns an approval URL. Approval must complete before provisioning can continue.
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowScimGuideModal(false);
                  onOpenUsersGroups?.();
                }}
                disabled={!onOpenUsersGroups}
              >
                Go to Users &amp; Groups Mapping
              </button>
              <button type="button" className="btn-primary" onClick={() => setShowScimGuideModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
